"use client"

import { useState, useEffect, useRef } from "react"
import { Lightbulb, Target, Compass, GraduationCap, Monitor, Gamepad2, Sparkles } from "lucide-react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import { useSeo } from "./hooks/useSeo"
import Navigation, { Footer } from "./Navigation"
import "./css/AboutPage.css"

const AboutPage = ({ onReturnHome }) => {
  // Get current language from context
  const { language } = useLanguage()

  // State for app configuration and about content
  const [appConfig, setAppConfig] = useState(null)
  const [aboutContent, setAboutContent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const bannerImage2 = aboutContent?.banner2?.image
  const [visibleSteps, setVisibleSteps] = useState(new Set())
  const journeyRef = useRef(null)
  const journeyIconRefs = useRef([])
  const [journeyTrail, setJourneyTrail] = useState({ path: "", width: 0, height: 0 })

  // Helper function to get localized text from content
  const t = (path, defaultText = "") => {
    // If appConfig isn't loaded yet, return the default text
    if (!appConfig) return defaultText

    // Split the path by dots to navigate nested objects
    const keys = path.split(".")
    let value = appConfig

    // Navigate through the object
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }

    // Return the localized text if it's an object with language keys
    if (value && typeof value === "object" && value[language.code]) {
      return value[language.code]
    }

    // Return the value if it's a string or the default text
    return typeof value === "string" ? value : value.en || defaultText
  }

  useSeo({
    path: "/about",
    title: language.code === "en" ? "About Techtok" : "Over Techtok",
    description:
      language.code === "en"
        ? "Techtok makes digital literacy fun and accessible for every child. Meet the team and the vision behind the platform."
        : "Techtok maakt digitale geletterdheid leuk en toegankelijk voor ieder kind. Maak kennis met het team en de visie achter het platform.",
  })

  // Load app configuration and about content
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        // Load the app configuration
        const companyId = new URLSearchParams(window.location.search).get("company") || null
        const config = await loadAppConfiguration(language.code, companyId, false)
        setAppConfig(config)

        // Apply accent color if provided
        if (config?.branding?.accentColor) {
          document.documentElement.style.setProperty("--accent-color", config.branding.accentColor)
        }

        // Load the about content
        const aboutResponse = await fetch(`/content/main/app-about.json`)
        if (!aboutResponse.ok) {
          throw new Error(`Failed to load about content: ${aboutResponse.status}`)
        }
        const aboutData = await aboutResponse.json()
        setAboutContent(aboutData)
      } catch (error) {
        console.error("Failed to load data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [language.code])

  // Journey map: reveal each waypoint as it scrolls into view, rather than
  // showing the whole path at once - this is the bit borrowed from the
  // game's own "progress along a map" feel (see AboutPage.css for the
  // path styling, kept in this site's own colors, not the game's).
  useEffect(() => {
    const steps = journeyRef.current?.querySelectorAll("[data-journey-step]")
    if (!steps || steps.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute("data-journey-step"))
            setVisibleSteps((prev) => new Set(prev).add(index))
          }
        })
      },
      { threshold: 0.3 },
    )
    steps.forEach((step) => observer.observe(step))
    return () => observer.disconnect()
  }, [aboutContent])

  // Draw the dashed trail through each node's real on-screen center, so it
  // touches every circle exactly the way the game's own map path does -
  // measured, not guessed, so it stays correct through the mobile zig-zag too.
  useEffect(() => {
    const container = journeyRef.current
    if (!container) return

    const computeTrail = () => {
      const containerRect = container.getBoundingClientRect()
      const points = journeyIconRefs.current
        .filter(Boolean)
        .map((el) => {
          const r = el.getBoundingClientRect()
          return {
            x: r.left + r.width / 2 - containerRect.left,
            y: r.top + r.height / 2 - containerRect.top,
          }
        })

      if (points.length < 2) {
        setJourneyTrail({ path: "", width: containerRect.width, height: containerRect.height })
        return
      }

      // Catmull-Rom through all the points, not a fresh vertical-tangent S
      // per segment - that forced every node into the same sharp turn.
      // This lets the curve's direction flow naturally from its neighbors.
      let d = `M ${points[0].x} ${points[0].y}`
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i - 1] || points[i]
        const p1 = points[i]
        const p2 = points[i + 1]
        const p3 = points[i + 2] || p2
        const cp1x = p1.x + (p2.x - p0.x) / 6
        const cp1y = p1.y + (p2.y - p0.y) / 6
        const cp2x = p2.x - (p3.x - p1.x) / 6
        const cp2y = p2.y - (p3.y - p1.y) / 6
        d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
      }
      setJourneyTrail({ path: d, width: containerRect.width, height: containerRect.height })
    }

    computeTrail()
    const settleTimeout = setTimeout(computeTrail, 350)
    const resizeObserver = new ResizeObserver(computeTrail)
    resizeObserver.observe(container)
    return () => {
      clearTimeout(settleTimeout)
      resizeObserver.disconnect()
    }
  }, [aboutContent, visibleSteps])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  // Map section icons based on index
  const getSectionIcon = (index) => {
    switch (index) {
      case 0:
        return <Lightbulb />
      case 1:
        return <Target />
      case 2:
        return <Compass />
      default:
        return <Lightbulb />
    }
  }

  // One themed icon per journey waypoint, in step order
  const journeyIcons = [Sparkles, GraduationCap, Monitor, Gamepad2, Lightbulb]
  const getJourneyIcon = (index) => {
    const Icon = journeyIcons[index % journeyIcons.length]
    return <Icon />
  }

  // Handle navigation
  const handleNavigation = (page) => {
    if (page === "home") {
      onReturnHome()
    }
  }

  return (
    <div className="about-selector-container">
      <Navigation appConfig={appConfig} currentPage="about" onNavigate={handleNavigation} t={t} />

      {/* About Content */}
      <main className="main-content landing-selector-container">

       {/* Our Story Section */}
       <section className="hero-section-top">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 items-center px-[40px] py-[0px]">
              {/* Text Content - Left Side */}
              <div className="text-left">
                <h1 className="hero-title">{aboutContent.story.subtitle?.[language.code]}</h1>
                <p className="hero-subtitle">
                    {aboutContent.story.founders.content?.[language.code]}
                </p>
              </div>

              {bannerImage2 && (
                <img
                  src={bannerImage2 || "/placeholder.svg"}
                  alt="Banner"
                  className="mx-auto mb-6 w-full max-w-3xl rounded-2xl"
                  style={{ objectFit: "cover" }}
                />
              )}
            </div>
          </div>


        </section>

        {/* Our Journey - a map of how Techtok actually got here, styled like
            a Missies mission map: icon nodes on a dashed trail, caption
            tucked right under each one. */}
        {aboutContent?.story?.sections && (
        <section className="journey-section">
          <div className="journey-section-header">
            <h2 className="journey-section-title">{aboutContent.story.heading?.[language.code]}</h2>
            <p className="journey-section-subtitle">{aboutContent.story.content?.[language.code]}</p>
          </div>
          <div className="story-journey" ref={journeyRef}>
            {journeyTrail.path && (
              <svg
                className="story-journey-trail"
                viewBox={`0 0 ${journeyTrail.width} ${journeyTrail.height}`}
                aria-hidden="true"
              >
                <path
                  d={journeyTrail.path}
                  fill="none"
                  stroke="#fff"
                  strokeWidth="11"
                  strokeLinecap="round"
                  strokeDasharray="16 16"
                />
              </svg>
            )}
            {aboutContent.story.sections.map((step, index) => (
              <div
                key={index}
                data-journey-step={index}
                className={`story-journey-step${visibleSteps.has(index) ? " is-visible" : ""}${index % 2 === 1 ? " story-journey-step-right" : ""}`}
              >
                <div className="story-journey-node">
                  <div className="story-journey-icon" ref={(el) => (journeyIconRefs.current[index] = el)}>
                    {getJourneyIcon(index)}
                  </div>
                  <div className="story-journey-label">
                    <h3 className="story-journey-heading">{step.heading?.[language.code]}</h3>
                    <p className="story-journey-text">{step.content?.[language.code]}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
        )}

        {/* Vision Sections */}
        <div className="about-sections about-main">
            {aboutContent?.sections?.map((section, index) => (
              <section key={index} className="about-section">
                <div className="section-icon">{getSectionIcon(index)}</div>
                <h2 className="section-heading">{section.heading?.[language.code] || `Section ${index + 1}`}</h2>
                <p className="section-content">{section.content?.[language.code] || ""}</p>
              </section>
            ))}
          </div>


                {/* Team Section */}
          {aboutContent?.team && (
            <section className="team-main-about">
              <div className="about-me-container">
                <div className="about-me-content" style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
                  {aboutContent.team.map((person, idx) => (
                    <div key={person.title?.[language.code]} style={{ flex: "1 1 320px", maxWidth: 450 }}>
                      <div className="about-me-image-container">
                        <img
                          src={person.image || "/placeholder.svg"}
                          alt={person.title?.[language.code]}
                          className="about-me-image"
                        />
                      </div>
                      <div className="about-me-text">
                        <h3 className="about-me-title">{person.title?.[language.code]}</h3>
                        <h4 className="about-me-subtitle">{person.subtitle?.[language.code]}</h4>
                        <p className="about-me-description">{person.content?.[language.code]}</p>
                        {person.linkUrl && (
                          <a href={person.linkUrl} target="_blank" rel="noopener noreferrer" className="about-me-link">
                            {person.linkText?.[language.code]}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

               


        {/* Exit / contact section */}
        <div className="exit-banner">
      <div className="coming-soon-section">
        <h1 className="hero-title">{aboutContent.contact.heading?.[language.code] || "Get in Touch"}</h1>
        <p className="hero-subtitle">
        {t(
              "comingSoon.contact",
              language.code === "nl"
                ? "Start met een gratis proefweek, kies daarna het plan dat bij jullie past. Geen abonnement, geen kleine lettertjes."
                : "Start with a free trial week, then pick the plan that fits your family. No subscription, no hidden fees.",
            )}
          </p>

          <a href="/portal/register.php" className="main-action-button">
            {language.code === "nl" ? "Start gratis proefweek" : "Start free trial"}
          </a>
          <p className="about-contact-email">
            {language.code === "nl" ? "Liever eerst een vraag stellen? " : "Prefer to ask a question first? "}
            <a href={`mailto:${aboutContent.contact.email}`}>{aboutContent.contact.email}</a>
          </p>
        </div>

        


        </div>
      </main>

      <Footer appConfig={appConfig} t={t} />
    </div>
  )
}

export default AboutPage
